'use client';
import { FC } from 'react';
import { Box, Typography, Stack, Paper, IconButton, Button } from '@mui/material';
import Link from 'next/link';
import { ArrowForward } from '@mui/icons-material';
import ReadOnlyFooter from '@/components/details/ReadOnlyFooter';
import DetailField from '@/components/common/DetailField';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useUserResolution } from '@/lib/options/useUserResolution';
import { formatDate } from '@/lib/utils/dateUtils';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { SampleDto } from '@/api/ApiClient';
import { sampleHasSubstrateDetailsData } from '@/lib/utils/sampleUtils';
import { SampleOwnershipFields } from '@/components/details/SampleOwnershipFields';

export const selectOptions: OptionKey[] = [
  'sampleDropdownChoices',
  'researcherOptions',
  'facultyOptions',
  'materials',
  'synthesisTechniques',
  'synthesisTechniquesLeafOnly',
  'sampleReportingCategory',
];

const SampleDetailsReadOnly: FC = () => {
  const { options, loading: optionsLoading } = useSelectOptions(selectOptions);
  const { getSampleData } = useTree();
  const { selectedNode } = useTreeSelection();
  const { resolveUser } = useUserResolution();
  const systemConfig = useSystemConfig();

  // Get sample data from tree store
  const sample = selectedNode ? getSampleData(selectedNode) : undefined;

  if (!sample) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          Loading sample details...
        </Typography>
      </Box>
    );
  }

  if (optionsLoading) {
    return <Typography variant="body1">Loading sample details...</Typography>;
  }

  const getMaterialName = (id: number) => {
    const material = options.materials?.find((m) => m.id === id);
    return material?.label ?? material?.name ?? id.toString();
  };

  const getTechniqueName = (id: string) => options.synthesisTechniques?.find((m) => m.id === id)?.name || id;

  const techniqueUsesSubstrates =
    options.synthesisTechniquesLeafOnly?.find((m) => m.id === sample.techniqueId)?.growOnSubstrate ?? true;

  // Type guard for startingMaterials array - API returns array but type definition is incorrect
  const startingMaterials = Array.isArray(sample.sampleAsSubstrate)
    ? sample.sampleAsSubstrate
    : sample.sampleAsSubstrate
      ? [sample.sampleAsSubstrate]
      : [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h6" gutterBottom>
            General Details
          </Typography>
          <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {/* ID Field */}
                <DetailField
                  label="ID"
                  value={
                    <Link
                      href={`${systemConfig?.basePath}/samples/${encodeURIComponent(sample.sampleLabel || String(sample.id))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sample-link"
                    >
                      {sample.sampleLabel || sample.id}
                    </Link>
                  }
                  isReadOnly={true}
                />

                {/* Alias */}
                {sample.refSampleLabel ? (
                  <DetailField label="Alias" value={sample.refSampleLabel} isReadOnly={true} />
                ) : null}

                {/* Project / data-package ownership */}
                <SampleOwnershipFields
                  sample={sample}
                  projectLabels={(sample.projectLabels ?? []).filter((l): l is string => !!l)}
                  basePath={systemConfig?.basePath}
                  fieldWidth={{ xs: '100%', sm: '32%' }}
                />

                {/* Technique Section */}
                {sample.techniqueId ? (
                  <DetailField label="Technique" value={getTechniqueName(sample.techniqueId)} isReadOnly={true} />
                ) : null}

                {/* Owner Section */}
                {sample.userId ? (
                  <Box sx={{ width: { xs: '100%', sm: '32%' } }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Made by
                    </Typography>
                    <Typography variant="body1">{resolveUser(sample.userId)}</Typography>
                  </Box>
                ) : null}

                {/* Reporting Category is live-inventory bookkeeping; omit on published copies. */}
                {sample.reportingCategory && !sample.isSnapshot ? (
                  <DetailField label="Reporting Category" value={sample.reportingCategory.label} isReadOnly={true} />
                ) : null}

                {/* Growth Date Section */}
                {sample.creationDate ? (
                  <Box sx={{ width: { xs: '100%', sm: '32%' } }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Inventory Date
                    </Typography>
                    <Typography variant="body1">{formatDate(sample.creationDate)}</Typography>
                  </Box>
                ) : null}

                {/* Sample Location Section */}
                {sample.sampleLocation ? (
                  <Box sx={{ width: { xs: '100%', sm: '32%' } }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sample Location
                    </Typography>
                    <Typography variant="body1">{sample.sampleLocation}</Typography>
                  </Box>
                ) : null}

                {/* Grown Layer Material Section */}
                {Array.isArray(sample.materialIds) && sample.materialIds.length > 0 ? (
                  <Box sx={{ width: { xs: '100%', sm: '32%' } }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Grown Layer Material(s)
                    </Typography>
                    <Typography variant="body1">
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            sample.materialIds?.map((id) => getMaterialName(Number(id))).join(', ') ?? 'No Growth Yet',
                        }}
                      />
                    </Typography>
                  </Box>
                ) : null}

                {/* Quality Section */}
                {sample.quality ? <DetailField label="Quality" value={sample.quality} isReadOnly={true} /> : null}

                {/* Size Section */}
                {sample.size ? <DetailField label="Size" value={sample.size} isReadOnly={true} /> : null}

                {/* Thickness */}
                {sample.thickness ? <DetailField label="Thickness" value={sample.thickness} isReadOnly={true} /> : null}

                {/* Sample Holder Section */}
                {sample.sampleHolder ? (
                  <DetailField label="Sample Holder" value={sample.sampleHolder} isReadOnly={true} />
                ) : null}

                {/* Description */}
                {sample.description ? (
                  <DetailField
                    label="Description"
                    value={<span dangerouslySetInnerHTML={{ __html: sample.description as TrustedHTML }} />}
                    isReadOnly={true}
                    width={{ width: '100%' }}
                  />
                ) : null}

                {/* Split from */}
                {sample.splitFrom ? (
                  <Box sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Split from
                      </Typography>
                      <Link href={`${systemConfig?.basePath}/samples/${sample.splitFrom.sampleLabel}`} passHref>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" component="span">
                            <ArrowForward />
                          </IconButton>
                          <Typography variant="subtitle2" color="text.secondary">
                            {sample.splitFrom.sampleLabel}
                          </Typography>
                        </Box>
                      </Link>
                    </Box>
                  </Box>
                ) : null}
              </Box>
            </Stack>
          </Paper>

          {/* StartingMaterials Section */}
          {startingMaterials.length > 0 ? (
            <>
              <Typography variant="h6" gutterBottom>
                StartingMaterials
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {startingMaterials.map((startingMaterial: SampleDto, index: number) => (
                  <Paper
                    key={startingMaterial.id}
                    elevation={0}
                    variant="outlined"
                    sx={{
                      p: 2,
                      flex: {
                        xs: '1 1 100%', // Full width on extra small screens
                        sm: '1 1 100%', // Full width on small screens
                        md: startingMaterials.length > 1 ? '1 1 calc(50% - 8px)' : '1 1 100%', // Side-by-side on medium+ screens if 2 startingMaterials
                      },
                      minWidth: { xs: 0, md: startingMaterials.length > 1 ? '300px' : 'auto' },
                    }}
                  >
                    <Stack spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {startingMaterials.length > 1 ? `StartingMaterial #${index + 1}` : 'StartingMaterial'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap' }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {startingMaterial.sampleLabel}
                            {startingMaterial.refSampleLabel ? (
                              <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                ({startingMaterial.refSampleLabel})
                              </Typography>
                            ) : null}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                              Material:
                            </Typography>
                            <Typography variant="body2">
                              {startingMaterial.materials && startingMaterial.materials.length > 0 ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: startingMaterial.materials.join(', '),
                                  }}
                                />
                              ) : (
                                '(not specified)'
                              )}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Box>
                        <Link
                          href={`${systemConfig?.basePath}/samples/${startingMaterial.sampleLabel}`}
                          passHref
                          style={{ textDecoration: 'none' }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            endIcon={<ArrowForward />}
                            sx={{
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                            }}
                          >
                            Go to Sample
                          </Button>
                        </Link>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            </>
          ) : null}

          {techniqueUsesSubstrates && startingMaterials.length === 0 && sampleHasSubstrateDetailsData(sample) ? (
            <>
              <Typography variant="h6" gutterBottom>
                Substrate Details
              </Typography>
              <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {/* Name */}
                  {sample.substrateName ? (
                    <DetailField label="Name" value={sample.substrateName} isReadOnly={true} />
                  ) : null}

                  {/* Vendor */}
                  {sample.substrateVendor ? (
                    <DetailField label="Vendor" value={sample.substrateVendor} isReadOnly={true} />
                  ) : null}

                  {/* Lot */}
                  {sample.substrateLot ? (
                    <DetailField label="Lot" value={sample.substrateLot} isReadOnly={true} />
                  ) : null}

                  {/* Orientation */}
                  {sample.substrateOrientation ? (
                    <DetailField label="Orientation" value={sample.substrateOrientation} isReadOnly={true} />
                  ) : null}

                  {/* Purity */}
                  {sample.substratePurity !== undefined ? (
                    <DetailField label="Purity" value={`${sample.substratePurity}%`} isReadOnly={true} />
                  ) : null}

                  {/* Substrate Material */}
                  {sample.substrateMaterialIds?.length ? (
                    <DetailField
                      label="Substrate Material"
                      value={
                        <span
                          dangerouslySetInnerHTML={{
                            __html:
                              sample.substrateMaterialIds?.map((id) => getMaterialName(Number(id))).join(', ') ??
                              'Not specified',
                          }}
                        />
                      }
                      isReadOnly={true}
                    />
                  ) : null}
                </Box>
              </Paper>
            </>
          ) : null}
        </Stack>
      </Box>

      {/* Footer - Fixed at bottom */}
      <Box sx={{ flexShrink: 0 }}>
        <ReadOnlyFooter
          lastChangedDate={sample.lastChangedDate}
          lastChangedBy={sample.lastChangedByUser || 'Unknown'}
          status={sample.status || 'Draft'}
        />
      </Box>
    </Box>
  );
};

export default SampleDetailsReadOnly;
